/**
 * Plan capability matrix.
 *
 * Buyer catalog:
 *   foodMoverLite      — food runs (Lite)
 *   foodDeliveryPro    — home delivery only (standalone)
 *   foodMover          — food runs + pro ops
 *   Home Delivery      — Stripe add-on (+$250) on Lite or Pro (not a plan key)
 *
 * Legacy internal keys (product-id mapping only; prefer base plan + home_delivery_addon):
 *   foodMoverLiteHome / foodMoverProHome
 *
 * foodRuns  — food-run scheduling / multi-recipient rescue logistics
 * homeDelivery — distributions / home delivery ops
 * proOps — Food Mover Pro extras
 * basicOps — donor-style Basic features
 */

const PLAN_CAPABILITIES = {
  free: {
    foodRuns: false,
    homeDelivery: false,
    proOps: false,
    basicOps: false,
  },
  basic: {
    foodRuns: false,
    homeDelivery: false,
    proOps: false,
    basicOps: true,
  },
  foodMoverLite: {
    foodRuns: true,
    homeDelivery: false,
    proOps: false,
    basicOps: true,
  },
  foodDeliveryPro: {
    foodRuns: false,
    homeDelivery: true,
    proOps: false,
    basicOps: true,
  },
  // Legacy bundle SKU — prefer base + home_delivery_addon flag
  foodMoverLiteHome: {
    foodRuns: true,
    homeDelivery: true,
    proOps: false,
    basicOps: true,
  },
  foodMover: {
    foodRuns: true,
    homeDelivery: false,
    proOps: true,
    basicOps: true,
  },
  // Legacy bundle SKU — prefer base + home_delivery_addon flag
  foodMoverProHome: {
    foodRuns: true,
    homeDelivery: true,
    proOps: true,
    basicOps: true,
  },
  freshcue: {
    foodRuns: false,
    homeDelivery: false,
    proOps: false,
    basicOps: true,
  },
};

const DEFAULT_CAPABILITIES = PLAN_CAPABILITIES.free;

const getPlanCapabilities = (planKey) =>
  PLAN_CAPABILITIES[planKey] || DEFAULT_CAPABILITIES;

const hasPlanCapability = (planKey, capability) =>
  !!getPlanCapabilities(planKey)[capability];

const plansWithCapability = (capability) =>
  Object.keys(PLAN_CAPABILITIES).filter(
    (key) => PLAN_CAPABILITIES[key][capability]
  );

const isOpsPlan = (planKey, { homeDeliveryAddon } = {}) =>
  hasPlanCapability(planKey, 'foodRuns') ||
  hasPlanCapability(planKey, 'homeDelivery') ||
  !!homeDeliveryAddon;

const isFoodMoverLitePlan = (planKey) =>
  hasPlanCapability(planKey, 'foodRuns') &&
  !hasPlanCapability(planKey, 'proOps');

/** Base Food Mover plans that accept the Home Delivery Stripe add-on */
const HOME_DELIVERY_ADDON_ELIGIBLE_PLANS = ['foodMoverLite', 'foodMover'];

/** Legacy: base → old bundle key (migration / display only) */
const HOME_DELIVERY_ADDON_PLAN_MAP = {
  foodMoverLite: 'foodMoverLiteHome',
  foodMover: 'foodMoverProHome',
};

const HOME_DELIVERY_ADDON_BASE_PLAN_MAP = {
  foodMoverLiteHome: 'foodMoverLite',
  foodMoverProHome: 'foodMover',
};

const getHomeDeliveryAddonTargetPlan = (planKey) =>
  HOME_DELIVERY_ADDON_PLAN_MAP[planKey] || null;

const getHomeDeliveryAddonBasePlan = (planKey) =>
  HOME_DELIVERY_ADDON_BASE_PLAN_MAP[planKey] || null;

const canAddHomeDeliveryAddon = (planKey) =>
  HOME_DELIVERY_ADDON_ELIGIBLE_PLANS.includes(planKey);

const isHomeDeliveryAddonPlan = (planKey) =>
  Boolean(getHomeDeliveryAddonBasePlan(planKey));

/**
 * Normalize a raw plan key from product ID: legacy *Home → base plan + addon.
 */
const normalizePlanKey = (planKey) => {
  const base = getHomeDeliveryAddonBasePlan(planKey);
  if (base) {
    return { planKey: base, homeDeliveryAddon: true, legacyBundle: true };
  }
  return {
    planKey: planKey || 'free',
    homeDeliveryAddon: false,
    legacyBundle: false,
  };
};

/**
 * Effective home delivery access:
 * - Food Delivery Pro (plan capability), or
 * - home_delivery_addon flag on Lite/Pro, or
 * - legacy *Home plan key, or
 * - distributions_enabled grandfather flag
 */
const hasEffectiveHomeDelivery = (
  planKey,
  { homeDeliveryAddon, distributionsEnabled } = {}
) => {
  if (hasPlanCapability(planKey, 'homeDelivery')) return true;
  if (getHomeDeliveryAddonBasePlan(planKey)) return true;
  if (
    homeDeliveryAddon &&
    (planKey === 'foodMoverLite' ||
      planKey === 'foodMover' ||
      planKey === 'foodMoverLiteHome' ||
      planKey === 'foodMoverProHome')
  ) {
    return true;
  }
  if (distributionsEnabled) return true;
  return false;
};

const getEffectivePlanCapabilities = (
  planKey,
  { homeDeliveryAddon, distributionsEnabled } = {}
) => {
  const { planKey: baseKey } = normalizePlanKey(planKey);
  const caps = { ...getPlanCapabilities(baseKey) };
  if (
    hasEffectiveHomeDelivery(planKey, {
      homeDeliveryAddon,
      distributionsEnabled,
    })
  ) {
    caps.homeDelivery = true;
  }
  return caps;
};

module.exports = {
  PLAN_CAPABILITIES,
  getPlanCapabilities,
  hasPlanCapability,
  plansWithCapability,
  isOpsPlan,
  isFoodMoverLitePlan,
  HOME_DELIVERY_ADDON_ELIGIBLE_PLANS,
  HOME_DELIVERY_ADDON_PLAN_MAP,
  HOME_DELIVERY_ADDON_BASE_PLAN_MAP,
  getHomeDeliveryAddonTargetPlan,
  getHomeDeliveryAddonBasePlan,
  canAddHomeDeliveryAddon,
  isHomeDeliveryAddonPlan,
  normalizePlanKey,
  hasEffectiveHomeDelivery,
  getEffectivePlanCapabilities,
};
